import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import { IconAlert, IconLoader } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface MovementProduct {
  id: string;
  name: string;
  code: string | null;
  unit: string;
}

interface Warehouse {
  id: string;
  name: string;
}

interface Movement {
  occurred_at: string;
  warehouse_id: string;
  delta: number;
  quantity_before: number;
  quantity_after: number;
  movement_type: string;
  reference: string | null;
}

interface MovementsPage {
  items: Movement[];
  total: number;
}

function fmtQty(n: number): string {
  return parseFloat(n.toFixed(3)).toLocaleString("cs-CZ");
}

export default function StockMovements() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<MovementProduct[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [searchParams] = useSearchParams();
  const [productId, setProductId] = useState(searchParams.get("product") ?? "");
  const [warehouseId, setWarehouseId] = useState("all");
  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);
  const [items, setItems] = useState<Movement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const whName = (id: string) => warehouses.find(w => w.id === id)?.name ?? "—";
  const product = products.find(p => p.id === productId);

  useEffect(() => {
    Promise.all([
      api.get<MovementProduct[]>("/inventory/movement-products").then(r => r.data),
      api.get<Warehouse[]>("/warehouses").then(r => r.data),
    ]).then(([prods, whs]) => {
      setProducts(prods);
      setWarehouses(whs);
    }).catch(() => setErr(t("movements.loadError")));
  }, [t]);

  const load = useCallback(async () => {
    if (!productId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        product_id: productId,
        page: String(pageIndex + 1),
        size: String(pageSize),
      });
      if (warehouseId !== "all") params.set("warehouse_id", warehouseId);
      const { data } = await api.get<MovementsPage>(`/inventory/movements?${params}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setErr(apiError(e, t("movements.loadError")));
    } finally {
      setLoading(false);
    }
  }, [productId, warehouseId, pageIndex, pageSize, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPageIndex(0); }, [productId, warehouseId, pageSize]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div className="page-header">
        <h1>{t("movements.title")}</h1>
        <p>{t("movements.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
        <div style={{ width: 320, flexShrink: 0 }}>
          <SearchableSelect
            value={productId}
            onChange={setProductId}
            options={products.map(p => ({
              value: p.id,
              label: p.code ? `${p.name} (${p.code})` : p.name,
            }))}
            placeholder={t("movements.selectProduct")}
            searchPlaceholder={t("movements.searchProduct")}
          />
        </div>
        <div style={{ width: 200, flexShrink: 0 }}>
          <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
            <option value="all">{t("movements.allWarehouses")}</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      </div>

      {!productId ? (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>
          {t("movements.selectProductHint")}
        </div>
      ) : loading && items.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>
      ) : items.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>
          {t("movements.noMovements")}
        </div>
      ) : (
        <div className="card" style={{ opacity: loading ? 0.6 : 1 }}>
          <table>
            <thead>
              <tr>
                <th>{t("movements.colDate")}</th>
                <th>{t("movements.colWarehouse")}</th>
                <th style={{ textAlign: "right" }}>{t("movements.colBefore")}</th>
                <th style={{ textAlign: "right" }}>{t("movements.colAfter")}</th>
                <th style={{ textAlign: "right" }}>{t("movements.colDelta")}</th>
                <th>{t("movements.colType")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m, i) => (
                <tr key={i}>
                  <td style={{ color: "var(--muted)", fontSize: 13 }}>
                    {new Date(m.occurred_at).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td>{whName(m.warehouse_id)}</td>
                  <td style={{ textAlign: "right", color: "var(--muted)" }}>{fmtQty(m.quantity_before)} {product?.unit}</td>
                  <td style={{ textAlign: "right" }}>{fmtQty(m.quantity_after)} {product?.unit}</td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: m.delta >= 0 ? "var(--green)" : "var(--red)" }}>
                    {m.delta >= 0 ? "+" : ""}{fmtQty(m.delta)} {product?.unit}
                  </td>
                  <td>
                    {t(`movements.type.${m.movement_type}`)}
                    {m.reference && <span style={{ color: "var(--muted)", fontSize: 12 }}> · {m.reference}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
              <span>{t("movements.perPage")}</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: "auto" }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>· {t("movements.total", { total })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <button className="ghost" disabled={pageIndex === 0} onClick={() => setPageIndex(i => i - 1)}>‹ {t("movements.prev")}</button>
              <span style={{ color: "var(--muted)" }}>{pageIndex + 1} / {pageCount}</span>
              <button className="ghost" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(i => i + 1)}>{t("movements.next")} ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
